import { Navigate, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useMe } from "@/api/queries/auth";
import { PageLoading } from "@/components/states/PageLoading";
import { PermissionState } from "@/components/states/PermissionState";
import { RouteErrorBoundary } from "@/components/states/RouteErrorBoundary";
import { useClienteActivo } from "@/hooks/useClienteActivo";
import { clienteInicial } from "@/utils/clientes";
import { leerRecientes } from "@/utils/recientes";
import { NotFoundPage } from "./NotFoundPage";
import { destinoDeRedireccion, type RedireccionAntigua } from "./legacyRedirects";
import { rutaDeCliente, seccionDeRuta } from "./routeConfig";

/** `/`: la agencia entra por el último cliente (o el primero); sin clientes, a `/clientes`. La tienda, a su Inicio. */
export function InicioRedirect() {
  const { data: me } = useMe();
  if (!me) return <PageLoading />;
  const cliente = clienteInicial(me.clients, { pedido: null, recientes: leerRecientes(me.principal.user_id) });
  if (cliente) return <Navigate to={rutaDeCliente(cliente.slug, "inicio")} replace />;
  return <Navigate to={me.principal.role === "agency" ? "/clientes" : "/entrar"} replace />;
}

/** Ruta antigua → misma pantalla del cliente de `?business_id` (si hay acceso), del último usado o del primero. */
export function RedireccionAntigua({ redireccion }: { redireccion: RedireccionAntigua }) {
  const { data: me } = useMe();
  const params = useParams();
  const [searchParams] = useSearchParams();
  if (!me) return <PageLoading />;
  const cliente = clienteInicial(me.clients, { pedido: searchParams.get("business_id"), recientes: leerRecientes(me.principal.user_id) });
  if (!cliente) return <Navigate to={me.principal.role === "agency" ? "/clientes" : "/entrar"} replace />;
  return <Navigate to={destinoDeRedireccion(redireccion, { params, search: `?${searchParams.toString()}`, cliente })} replace />;
}

/** Rutas de agencia fuera de cualquier cliente (`/clientes`, `/agencia/equipo`): la tienda ve «Sin permiso». */
export function SoloAgencia() {
  const { data: me } = useMe();
  if (!me) return <PageLoading />;
  return me.principal.role === "agency" ? <Outlet /> : <PermissionState variante="tienda" />;
}

/**
 * Resuelve `/c/:cliente/…`: el slug contra `/auth/me`, el rol contra la sección y el contenido, montado con
 * la clave del cliente para que nada de un cliente sobreviva al cambio al siguiente.
 */
export function ClienteScope() {
  const activo = useClienteActivo();
  const { data: me } = useMe();
  const location = useLocation();

  if (activo.estado === "cargando" || !me) return <PageLoading />;
  if (activo.estado !== "activo") {
    const porId = me.clients.find((c) => c.business_id === activo.slug);
    if (porId) {
      const pathname = location.pathname.replace(`/c/${encodeURIComponent(activo.slug ?? "")}`, `/c/${porId.slug}`);
      return <Navigate to={{ pathname, search: location.search }} replace />;
    }
    // La tienda no puede distinguir «otro cliente» de «página que no existe».
    return <NotFoundPage variante={me.principal.role === "agency" ? "cliente" : "pagina"} />;
  }

  const { cliente, rol } = activo;
  if (!cliente.active) return <PermissionState variante="agencia" cliente={cliente.name} />;
  const resto = location.pathname.split(`/c/${encodeURIComponent(cliente.slug)}/`)[1] ?? "";
  const seccion = seccionDeRuta(resto);
  if (seccion && !seccion.rolesPermitidos.includes(rol)) return <PermissionState variante="tienda" />;

  return (
    <RouteErrorBoundary key={`${cliente.business_id}:${location.pathname}`}>
      <Outlet key={cliente.business_id} />
    </RouteErrorBoundary>
  );
}
